import { useMemo, useState, useRef, useCallback } from "react";
import { bool, string, number } from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { IconButton } from "@material-ui/core";
import { MoreHoriz } from "@material-ui/icons";
import { ArrowRight, Monitor } from "@assets";
import { PopMenu, MenuButton, Button } from "@components";
import { editor, pdfEditor, sessions } from "@paths";
import useStyle from "./ProjectActionMenu.style";
import { LOCKED_FEATURES } from "@utils";
import { useFeatureGuardian, useStores } from "@hooks";
import classnames from "classnames";
import {
  DuplicateProject,
  ArchiveProject,
  DeleteProject,
} from "./buttons";
import { checkBudgetTrackingQuery, checkSetUpQuery } from "@query";
import { zip } from "@repo/utils";
import { JiraExportAndBTInfoDialog } from "@dialogs";
import { Jira } from "@assets";

export const ProjectActionMenu = ({
  id,
  uuid,
  isOwner,
  isContractor,
  isBusiness,
  isMini,
  onlyDangerousActions,
  fullButton,
  name,
  projectBtSetUp,
  projectBtExported,
  hasComments,
}) => {
  const classes = useStyle();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userStore } = useStores();

  const { checkAccess: checkRecordAccess, FeatureAlert: RecordFeatureAlert } =
    useFeatureGuardian(LOCKED_FEATURES.RECORDINGS);

  const anchorRef = useRef(null);
  const [visible, show] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const useBT = useMemo(() => (
    Boolean(localStorage.getItem("new_jira_export"))
  ), [])

  const clickAwayHandler = useCallback(() => {
    show(false);
  }, []);

  const openMenu = useCallback(() => {
    show(!visible);
  }, [visible]);

  const openEditor = useCallback(() => {
    if (!uuid) return;
    navigate(editor(uuid));
  }, [uuid, navigate]);

  const openPdfEditor = useCallback(() => {
    if (!uuid) return;
    navigate(pdfEditor(uuid));
  }, [uuid, navigate, isMini]);

  const openSessionList = useCallback(async () => {
    if (!uuid) return;
    const allowed = await checkRecordAccess();
    if (!allowed) return;
    navigate(sessions(uuid));
  }, [uuid, navigate, checkRecordAccess]);

  const openBudgetTracking = useCallback((e) => {
    if (!uuid || !id) return;
    e.stopPropagation();
    checkBudgetTrackingQuery(
      id,
      zip(uuid),
      userStore.data.companyId,
      userStore.userId,
    );
  }, [uuid, id, userStore.data, userStore.userId]);

  const openSetUp = useCallback((e) => {
    e.stopPropagation();
    if (!uuid || !id) return;
    if (!projectBtExported) {
      setOpenModal(true);
    } else {
      checkSetUpQuery(id, zip(uuid), userStore.data.companyId);
    }
  }, [uuid, id, projectBtExported, userStore.data, userStore.userId]);

  const closeModal = useCallback(() => setOpenModal(false), []);

  return (
    <>
      {fullButton ? (
        <Button
          size="large"
          variant="contained"
          color="secondary"
          className="noDrag"
          ref={anchorRef}
          icon={<MoreHoriz />}
          onClick={openMenu}
        />
      ) : (
        <IconButton
          color="secondary"
          ref={anchorRef}
          onClick={openMenu}
          aria-label="show menu"
          className={classnames(classes.button, "noDrag")}
        >
          <MoreHoriz />
        </IconButton>
      )}

      <PopMenu
        show={visible}
        anchor={anchorRef.current}
        onClickAway={clickAwayHandler}
        style={{ width: 195 }}
      >
        {!onlyDangerousActions && (
          <MenuButton
            icon={<ArrowRight />}
            onClick={isMini ? openPdfEditor : openEditor}
          >
            {t("common.open")}
          </MenuButton>
        )}
        
        {!isContractor && (
          <MenuButton icon={<Jira />} onClick={openBudgetTracking}>
            {t("views.bt.export_title")}
          </MenuButton>
        )}

        {!isContractor && (!projectBtSetUp || !projectBtExported) && useBT && (
          <MenuButton icon={<Jira />} onClick={openSetUp}>
            {t("views.bt.import_title")}
          </MenuButton>
        )}
        {!isContractor && !onlyDangerousActions && !isMobile && isBusiness && (
          <MenuButton icon={<Monitor />} onClick={openSessionList}>
            {t("routes.recordings")}
          </MenuButton>
        )}
        {!isContractor && (
          <DuplicateProject name={name} uuid={uuid} hasComments={hasComments} />
        )}
        {isOwner && (
          <>
            <ArchiveProject uuid={uuid} />
            <DeleteProject uuid={uuid} />
          </>
        )}
        <RecordFeatureAlert />
      </PopMenu>
      {openModal && (
        <JiraExportAndBTInfoDialog
          openModal={openModal}
          title={t("views.bt.open_export_bt_first")}
          onClose={closeModal}
        />
      )}
    </>
  );
};

ProjectActionMenu.propTypes = {
  id: number.isRequired,
  uuid: string.isRequired,
  isOwner: bool.isRequired,
  isContractor: bool,
  isBusiness: bool.isRequired,
  isMini: bool.isRequired,
  onlyDangerousActions: bool,
  fullButton: bool,
  name: string,
  projectBtSetUp: bool,
  projectBtExported: bool,
  hasComments: bool,
};
